import * as React from 'react';
import {
  VendorViewModel,
  VendorSearchViewModel,
  TradeViewModel,
  TradeOptionsViewModel,
  TradeListItemViewModel
} from '@models/Vendor';
import { RouteComponentProps } from 'react-router';
import { SpinnerPage, TypeAheadComponent, CheckboxInline, Tabs, Tab } from '@common/components';
import { VendorInfoComponent } from 'src/vendor/_components/vendor-info/vendor-info.component';
import * as classes from './vendor-trade-maintenance.scss';
import { Control } from 'react-redux-form';
import { PageViewModel, OptionsViewModel } from '@models/common.models';
import ReactTable from 'react-table';
import { Link } from 'react-router-dom';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { TradeOverridesComponent } from './_components/trade-overrides.component';
import { TradeExceptionsComponent } from './_components/trade-exceptions.component';
import { history } from 'src';
import { DoubleSelectTableComponent } from '@common/components/double-select-table/double-select-table.component';
import { TradePriceAgreement } from './_components/trade-price-agreement.container';
import { TradeZipsContainer } from './trade-zips/_components/trade-zips.container';
import { TradeTripCharges } from 'src/vendor/_components/vendor-trade-maintenance/_components/trade-trip-charges.container';

export interface VendorTradeMaintenanceComponentProperties extends RouteComponentProps<{ id: number }> {
  readonly vendorVM: VendorViewModel;
  readonly getVendorDetails: (id: number) => void;
  readonly resetTradeMaintenanceForm: (currentTrade: TradeViewModel) => void;
  readonly saveVendorTrades: (vendorId: number, vendorTrades: TradeViewModel[]) => void;
  readonly updateVendorTrade: (vendorId: number, vendorTrade: TradeViewModel) => void;
  readonly toggleTradeActivation: (vendorId: number, vendorTradeId: number, activate: boolean) => void;
  readonly vendorZipsVM: OptionsViewModel[];

  readonly vendorBrandCertifications: OptionsViewModel[];
  readonly eligibleBrandCertifications: OptionsViewModel[];
  readonly getBrandCertifications: (vendorId: number) => void;
  readonly updateVendorBrandCertifications: (vendorId: number, data: OptionsViewModel[]) => void;

  readonly vendorCVIExceptions: OptionsViewModel[];
  readonly eligibleCVIs: OptionsViewModel[];
  readonly getCVIExceptions: (vendorId: number) => void;
  readonly isLoadingCVIExceptions: boolean;
  readonly isTogglingTradeActivation: boolean;
  readonly loadingVendor: boolean;
  readonly deleteVendorCVIExceptions: (vendorId: number, data: number[]) => void;
  readonly updateVendorCVIExceptions: (vendorId: number, data: number[]) => void;

  readonly vendorBrandExceptions: OptionsViewModel[];
  readonly getBrandExceptions: (vendorId: number) => void;
  readonly isLoadingBrandExceptions: boolean;
  readonly deleteVendorBrandExceptions: (vendorId: number, data: number[]) => void;
  readonly updateVendorBrandExceptions: (vendorId: number, data: number[]) => void;

  readonly tradeOptions: TradeOptionsViewModel[];
  readonly getTrades: () => void;
  readonly isLoadingBrandCertifications: boolean;
  readonly clearVendor: () => void;

  readonly onSaveClick: (values: any) => void;
  readonly vendorLookup: (query: string, id?: number) => Promise<PageViewModel<any>>;
}

interface VendorTradeMaintenanceComponentState {
  isActive: boolean;
  editingTrades: boolean;
  submitted: boolean;
  configuredTrades: TradeListItemViewModel[];
  cachedTrades: TradeListItemViewModel[];
  selected: {
    index: number | null;
    original: TradeOptionsViewModel | null;
    tradeVM: TradeViewModel | null;
  };
}

export class VendorTradeMaintenanceComponent extends React.Component<
  VendorTradeMaintenanceComponentProperties,
  VendorTradeMaintenanceComponentState
> {
  constructor(props: VendorTradeMaintenanceComponentProperties) {
    super(props);
    this.state = {
      isActive: true,
      editingTrades: false,
      configuredTrades: [],
      cachedTrades: [],

      selected: {
        index: null,
        original: null,
        tradeVM: null
      },
      submitted: false
    };
  }

  componentWillMount() {
    this.props.getTrades();
    this.loadData();
  }

  componentWillReceiveProps(nextProps: VendorTradeMaintenanceComponentProperties) {
    // Configuring trade selection after an update is received
    if (nextProps.vendorVM.vendorTrades && nextProps.vendorVM.vendorTrades !== this.props.vendorVM.vendorTrades) {
      this.loadTradeSelection(nextProps);
    }
    // Reset selection if vendor changes or when changes to trade options are received
    if (
      (this.props.vendorVM.id !== nextProps.vendorVM.id &&
        this.props.tradeOptions &&
        this.props.tradeOptions.length > 0) ||
      (this.props.vendorVM.id && nextProps.tradeOptions !== this.props.tradeOptions)
    ) {
      if (nextProps.vendorVM.vendorTrades) {
        this.loadTradeSelection(nextProps, true);
      }
    }
  }

  /**
   * Initialize all getters
   *
   */
  loadData = () => {
    if (this.props.match && this.props.match.params.id) {
      this.props.getVendorDetails(this.props.match.params.id);
      this.props.getBrandCertifications(this.props.match.params.id);
      this.props.getCVIExceptions(this.props.match.params.id);
      this.props.getBrandExceptions(this.props.match.params.id);
    }
  };

  /**
   * Switch to / from editing mode for adding / removing trades
   *
   */
  toggleEditingTrades = () => {
    this.setState({
      editingTrades: !this.state.editingTrades,
      selected: {
        index: null,
        original: null,
        tradeVM: null
      }
    });
  };

  /**
   * Save changes for added / removed trades
   *
   */
  saveTrades = () => {
    if (this.state.cachedTrades !== this.state.configuredTrades) {
      this.props.saveVendorTrades(this.props.match.params.id, this.state.configuredTrades.map(cur => cur.tradeVM));
      this.setState({
        cachedTrades: this.state.configuredTrades
      });
    }
    this.toggleEditingTrades();
  };

  /**
   * Configure selection / content for trades
   * Optional parameter 'reset' to clear selection
   *
   * @param {VendorTradeMaintenanceComponentProperties} props
   * @param {boolean} [reset]
   */
  loadTradeSelection = (props: VendorTradeMaintenanceComponentProperties, reset?: boolean) => {
    let loadList: TradeListItemViewModel[] = [];
    if (
      props.vendorVM.id &&
      props.vendorVM.vendorTrades &&
      props.vendorVM.vendorTrades.length > 0 &&
      props.tradeOptions
    ) {
      props.tradeOptions.map(obj => {
        return (
          props.vendorVM.vendorTrades &&
          props.vendorVM.vendorTrades.map((loadedTrade: TradeViewModel) => {
            if (obj.id === loadedTrade.tradeId) {
              loadList.push({
                tradeOption: obj,
                tradeVM: loadedTrade
              });
            }
          })
        );
      });
      reset
        ? this.setState({
            configuredTrades: loadList,
            cachedTrades: loadList,
            selected: {
              index: null,
              original: null,
              tradeVM: null
            }
          })
        : this.setState({
            configuredTrades: loadList,
            cachedTrades: loadList
          });
    }
  };

  /**
   * Typeahead render with given suggestion
   *
   * @param {VendorSearchViewModel} suggestion
   * @returns
   */
  renderVendorTypeahead = (suggestion: VendorSearchViewModel) => {
    return (
      <div>
        <div>Name: {suggestion.name}</div>
        {suggestion.email && <div>Email: {suggestion.email}</div>}
        <div>Location: {suggestion.group}</div>
      </div>
    );
  };

  /**
   * Whem adding / removing trades, shift from included / eligible sections
   *
   * @param {TradeOptionsViewModel} trade
   */
  toggleTradeSelection = (trade: TradeOptionsViewModel) => {
    const configuredTrades = this.state.configuredTrades;

    const exists = configuredTrades.find(obj => {
      return obj.tradeOption.id === trade.id;
    });

    const filtered = configuredTrades.filter(obj => {
      return obj.tradeOption !== trade;
    });

    if (exists) {
      this.setState({
        configuredTrades: filtered
      });
    } else {
      this.setState({
        configuredTrades: [
          {
            tradeOption: trade,
            tradeVM: {
              vendorId: +this.props.match.params.id,
              tradeId: trade.id,
              tradeActiveStatus: false
            } as TradeViewModel
          },
          ...configuredTrades
        ]
      });
    }
  };

  /**
   * Render method for each trade list item when adding / removing trades
   *
   * @param {TradeOptionsViewModel} row
   * @returns
   * @memberof VendorTradeMaintenanceComponent
   */
  renderTradeOption(row: TradeOptionsViewModel) {
    const label = <label>{row.value}</label>;
    return this.state.editingTrades ? (
      <CheckboxInline>
        <input
          onChange={e => e}
          onClick={() => {
            this.toggleTradeSelection(row);
          }}
          type="checkbox"
          checked={
            this.state.configuredTrades.filter(obj => {
              return obj.tradeOption.id === row.id;
            }).length > 0
          }
          value={row.id}
          key={row.id}
          disabled={false}
          className="pointer"
        />
        {label}
      </CheckboxInline>
    ) : (
      <>
        {label}
        <i className="fas fa-fw fr fa-chevron-right" />
      </>
    );
  }

  /**
   * Left panel / list of configured trades for a vendor
   * Also includes button for Add/Remove
   *
   * @returns
   * @memberof VendorTradeMaintenanceComponent
   */
  renderTradesLeftPanel() {
    // Show data only for given state (Current or Activate)
    const filteredData = this.state.configuredTrades.filter(
      trade => trade.tradeVM.tradeActiveStatus === this.state.isActive
    );
    const content =
      filteredData.length === 0 ? (
        <h3>Added Trades will appear here.</h3>
      ) : (
        <ReactTable
          key={filteredData.length}
          onPageChange={() => {
            Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
              element.scrollTop = 0;
            });
          }}
          className="-striped -highlight"
          defaultPageSize={filteredData.length}
          columns={[
            {
              accessor: 'original',
              Cell: (row: any) => this.renderTradeOption(row.original.tradeOption),
              Header: 'Selected Trades',
              sortable: false
            }
          ]}
          data={filteredData}
          showPagination={false}
          getTrProps={(state: any, rowInfo: any) => {
            if (!this.state.editingTrades && rowInfo && rowInfo.row) {
              return {
                onClick: (e: any) => {
                  this.setState({
                    selected:
                      this.state.selected.index === rowInfo.index
                        ? {
                            index: null,
                            original: null,
                            tradeVM: null
                          }
                        : {
                            index: rowInfo.index,
                            original: rowInfo.original.tradeOption,
                            tradeVM: rowInfo.original.tradeVM
                          }
                  });
                },
                className: `pointer ${rowInfo.index === this.state.selected.index && 'hover'}`
              };
            } else {
              return {};
            }
          }}
        />
      );
    return (
      <>
        {/* Only show this button for non-activated trades */}
        {!this.state.isActive && (
          <div className="row center-xs">
            <button
              className="btn btn-secondary m-10"
              type="button"
              onClick={() => (this.state.editingTrades ? this.saveTrades() : this.toggleEditingTrades())}
            >
              {this.state.editingTrades ? 'Save Trades' : 'Add/Remove Trades'}
            </button>
          </div>
        )}

        {content}
      </>
    );
  }

  /**
   * Render method for container with Trade list on left and content on right
   *
   * @returns
   * @memberof VendorTradeMaintenanceComponent
   */
  renderTradeContainer() {
    return (
      <div className={`row ${classes.tradeContainer}`}>
        <div className={`col-md-2 ${classes.leftPanel}`}>
          {this.props.loadingVendor || this.props.isTogglingTradeActivation ? (
            <SpinnerPage />
          ) : (
            this.renderTradesLeftPanel()
          )}
        </div>
        <div className={`col-md-10 ${classes.rightPanel}`}>
          {this.state.editingTrades ? this.renderEditingTradesRightPanel() : this.renderConfiguredTradesRightPanel()}
        </div>
      </div>
    );
  }

  /**
   * Render method for list of eligible to add trades
   *
   * @returns
   * @memberof VendorTradeMaintenanceComponent
   */
  renderEditingTradesRightPanel() {
    const filteredData =
      this.state.configuredTrades.length > 0
        ? this.props.tradeOptions.filter(obj => {
            const isSelected = this.state.configuredTrades.filter(selectedObj => {
              return selectedObj.tradeOption.id === obj.id;
            });
            return isSelected.length === 0;
          })
        : this.props.tradeOptions;
    return (
      <>
        <h3 className="text-left">Add Trades</h3>

        <div className="row">
          <div className="col-md-12">
            <ReactTable
              onPageChange={() => {
                Array.from(document.getElementsByClassName('rt-tbody')).forEach(element => {
                  element.scrollTop = 0;
                });
              }}
              className="-striped -highlight"
              key={filteredData.length}
              defaultPageSize={filteredData.length > 5 ? filteredData.length : 5}
              columns={[
                {
                  accessor: 'original',
                  Cell: (row: any) => this.renderTradeOption(row.original),
                  Header: 'Available Trades',
                  sortable: false,
                  filterMethod: (filter: any, row: any) => {
                    return (
                      String(row._original.value)
                        .toLowerCase()
                        .indexOf(filter.value.toLowerCase()) !== -1 ||
                      String(row._original.tradeAnalysisName)
                        .toLowerCase()
                        .indexOf(filter.value.toLowerCase()) !== -1
                    );
                  }
                }
              ]}
              data={filteredData}
              showPagination={false}
              filterable={true}
            />
          </div>
        </div>
      </>
    );
  }

  /**
   * Render method for currently selected trade content
   *
   * @returns
   * @memberof VendorTradeMaintenanceComponent
   */
  renderConfiguredTradesRightPanel() {
    return (
      <div className={classes.configuredTrades}>
        {!this.state.selected.tradeVM ? (
          <h2>Select a trade</h2>
        ) : (
          <>
            {this.state.selected.original && <h2>{this.state.selected.original.value}</h2>}
            <button className={`btn btn-primary ${classes.btnActivate}`} onClick={this.toggleTradeStatus}>{`${
              this.state.isActive ? 'Dea' : 'A'
            }ctivate Trade`}</button>
            <Tabs left={true}>
              <Tab title={'Trade Overrides'}>
                <TradeOverridesComponent
                  vendorVM={this.props.vendorVM}
                  currentTrade={this.state.selected.tradeVM}
                  reset={this.props.resetTradeMaintenanceForm}
                  updateVendorTrade={this.props.updateVendorTrade}
                />
              </Tab>
              <Tab title={'Zip Exceptions'} key={`${this.state.selected.tradeVM.tradeId}-exclusions`}>
                <TradeExceptionsComponent vendorVM={this.props.vendorVM} currentTrade={this.state.selected.tradeVM} />
              </Tab>
              <Tab title={'Trip Charges'}>
                <TradeTripCharges vendorVM={this.props.vendorVM} currentTrade={this.state.selected.tradeVM} />
              </Tab>
            </Tabs>
          </>
        )}
      </div>
    );
  }

  /**
   * Toggles trade between active / deactivated and deselects
   *
   */
  toggleTradeStatus = () => {
    if (this.state.selected && this.state.selected.tradeVM) {
      const tradeVM = this.state.selected.tradeVM;
      this.props.toggleTradeActivation(this.props.match.params.id, tradeVM.tradeId, !tradeVM.tradeActiveStatus);
      this.setState({
        selected: {
          index: null,
          original: null,
          tradeVM: null
        }
      });
    }
  };

  /**
   * Redirect upon selecting new vendor
   *
   * @param {number} id
   * @memberof VendorTradeMaintenanceComponent
   */
  redirectVendorTypeahead(id: number) {
    history.push(getRoute(RouteDefinitions.vendor.TradeMaintenace, { id: id }));
  }

  /**
   * Render for vendor tabs at top of page
   *
   * @returns
   */
  renderVendorContainer = () => {
    return (
      <Tabs left={true}>
        <Tab title={'Brand Exceptions'}>
          <DoubleSelectTableComponent
            eligibleData={this.props.eligibleBrandCertifications}
            eligibleLabel="Eligible Brands"
            storedData={this.props.vendorBrandExceptions}
            storedLabel="Excluded Brands"
            deleteCallback={(data: OptionsViewModel[]) => {
              this.props.deleteVendorBrandExceptions(this.props.match.params.id, data.map(brand => brand.id));
            }}
            saveCallback={(data: OptionsViewModel[]) => {
              this.props.updateVendorBrandExceptions(this.props.match.params.id, data.map(brand => brand.id));
            }}
          />
        </Tab>
        <Tab title={'CVI Exceptions'}>
          <DoubleSelectTableComponent
            eligibleData={this.props.eligibleCVIs}
            eligibleLabel="Eligible CVIs"
            storedData={this.props.vendorCVIExceptions}
            storedLabel="Excluded CVIs"
            deleteCallback={(data: OptionsViewModel[]) => {
              this.props.deleteVendorCVIExceptions(this.props.match.params.id, data.map(cvi => cvi.id));
            }}
            saveCallback={(data: OptionsViewModel[]) => {
              this.props.updateVendorCVIExceptions(this.props.match.params.id, data.map(cvi => cvi.id));
            }}
          />
        </Tab>
        <Tab title={'Brand Certification'}>
          <DoubleSelectTableComponent
            eligibleData={this.props.eligibleBrandCertifications}
            eligibleLabel="Eligible Brand Certifications"
            storedData={this.props.vendorBrandCertifications}
            storedLabel="Saved Brand Certifications"
            showWeight={true}
            saveCallback={(data: OptionsViewModel[]) => {
              data.forEach(element => {
                if (element.order === '') {
                  element.order = '1';
                }
              });
              this.props.updateVendorBrandCertifications(this.props.match.params.id, data);
            }}
          />
        </Tab>
        <Tab title={'Price Agreement'}>
          <TradePriceAgreement id={this.props.vendorVM.id} />
        </Tab>
        <Tab title={'Included Zips'}>
          <TradeZipsContainer vendorVM={this.props.vendorVM} keyVal={`vendor-${this.props.match.params.id}-zips`} />
        </Tab>
      </Tabs>
    );
  };

  /**
   * Switch between current / deactivated trades list
   *
   * @param {boolean} val
   */
  setActiveView = (val: boolean) => {
    this.setState({
      isActive: val,
      selected: {
        index: null,
        original: null,
        tradeVM: null
      }
    });
  };

  render() {
    if (
      !this.props.vendorVM.id ||
      this.props.eligibleBrandCertifications!.length === 0 ||
      this.props.isLoadingBrandCertifications ||
      this.props.isLoadingCVIExceptions ||
      this.props.isLoadingBrandExceptions
    ) {
      return <SpinnerPage />;
    }

    return (
      <div>
        <div className="row">
          <div className="col-xs-3">
            <Control.text
              model=".userGroup"
              component={(props: { value: ''; onChange: (event: VendorSearchViewModel) => void }) => {
                return (
                  <TypeAheadComponent
                    {...props}
                    onChange={(event: any) => {
                      this.redirectVendorTypeahead(event.id);
                      props.onChange(event.id);
                    }}
                    returnOption={true}
                    renderValue={this.renderVendorTypeahead}
                    valueKey={'id'}
                    labelKey={'email'}
                    getSuggestions={this.props.vendorLookup}
                    value={props.value}
                    placeholder="Vendor Search..."
                  />
                );
              }}
            />
          </div>
          <div className="col-xs-3 mt-10">
            <Link to={RouteDefinitions.vendor.Search}>Advanced Search</Link>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-4">
            <div className="row middle-xs">
              <h1>
                {this.props.vendorVM.companyName}{' '}
                <Link to={getRoute(RouteDefinitions.vendor.Edit, { id: +this.props.match.params.id })}>Edit</Link>
              </h1>
            </div>
          </div>
        </div>
        {this.renderVendorContainer()}
        <div className={classes.tabs}>
          <h2>Trades</h2>
          <div className={classes.toggle}>
            <button
              className={`btn btn-toggle-${this.state.isActive ? 'on' : 'off'}`}
              onClick={() => this.setActiveView(true)}
              disabled={this.state.editingTrades}
            >
              Current Trades
            </button>
            <button
              className={`btn btn-toggle-${!this.state.isActive ? 'on' : 'off'}`}
              onClick={() => this.setActiveView(false)}
            >
              Activate Trades
            </button>
          </div>
          {this.renderTradeContainer()}
        </div>
        <VendorInfoComponent isTabs={true} vendorVM={this.props.vendorVM} />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/vendor/_components/vendor-trade-maintenance/vendor-trade-maintenance.component.tsx